"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import type { GameType, Category } from "@/lib/types";
import { fetchGamesByRange } from "@/lib/games"
import Swal from "sweetalert2";
import { Loader2 } from "lucide-react";

interface GameGridProps {
  games: GameType[];
  categories?: Category[];
  selectedCategory?: string | null;
  expandable?: boolean;
  page?: number | null;
  errorMsg?: string | null;
  unit?: number;
}

export default function GameGrid({
  games,
  categories,
  selectedCategory,
  unit = 12, // Default value if not provided
  expandable = false,
  page,
  errorMsg: initialErrorMsg
}: GameGridProps) {
  const initialDisplayedGames = games;
  const [additionalDisplayedGames, setAdditionalDisplayedGames] = useState<GameType[]>([]);
  const [depth, setDepth] = useState(1);
  const [currentErrorMsg, setCurrentErrorMsg] = useState<string | null>(initialErrorMsg || null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const onCategoryButtonClicked = () => {
    setDepth(1);
    setAdditionalDisplayedGames([]);
    setCurrentErrorMsg(null); // Reset error on category change
  };

  const onLoadMoreGamesButtonClicked = async () => {
    if (isLoadingMore) return; // Prevent multiple clicks
    setIsLoadingMore(true);
    setCurrentErrorMsg(null); // Reset error before fetching

    try {
      const newGames = await fetchGamesByRange(selectedCategory ?? null, (depth * unit), (depth + 1) * unit - 1);
      if (newGames.length > 0) {
        setAdditionalDisplayedGames(prev => [...prev, ...newGames]);
        setDepth(depth + 1); // Increment depth only if games were fetched
      }
    } catch (err) {
      console.error("게임 데이터 가져오기 실패:", err);
      const errorText = err instanceof Error ? err.message : "게임 데이터를 불러오는 데 실패했습니다.";
      setCurrentErrorMsg(errorText); // Set error message to state
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Display error message using Swal when it changes
  useEffect(() => {
    if (currentErrorMsg) {
      Swal.fire({
        icon: "error",
        title: "오류 발생",
        text: currentErrorMsg,
        confirmButtonText: "확인",
      }).then(() => {
        setCurrentErrorMsg(null); // Reset error after showing
      });
    }
  }, [currentErrorMsg]);

  // Combine initial and additional games for rendering
  const allGames = [...initialDisplayedGames, ...additionalDisplayedGames];

  return (
    <div className="game-grid-container">
      {/* Category Buttons */}
      <div className="category-buttons mb-6 flex flex-wrap gap-2 justify-center">
        <Link href="/games">
          <Button
            onClick={onCategoryButtonClicked}
            variant={selectedCategory === null || selectedCategory === undefined ? "default" : "outline"}
            className={`h-8 px-4 text-sm rounded-lg transition-all duration-200 ease-in-out hover:scale-[1.02] active:scale-[0.98]`}
          >
            전체
          </Button>
        </Link>
        {(categories || [])
          .filter((category) => category.name !== "0") // Filter out category "0"
          .map((category) => (
            <Link key={category.name} href={`/games?category=${encodeURIComponent(category.name)}`}>
              <Button
                onClick={onCategoryButtonClicked}
                variant={selectedCategory === category.name ? "default" : "outline"}
                className={`h-8 px-4 text-sm rounded-lg transition-all duration-200 ease-in-out hover:scale-[1.02] active:scale-[0.98]`}
              >
                {category.name}
              </Button>
            </Link>
          ))}
      </div>

      {/* Game Card List */}
      {allGames.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
            {allGames.map((game) => (
              <Link key={game.id} href={`/games/${game.slug}`} className="block group">
                <Card className="overflow-hidden h-full rounded-lg transition-all duration-300 ease-in-out hover:shadow-xl hover:scale-[1.01]">
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <Image
                      src={game.thumbnail || "/placeholder.png"}
                      alt={game.title}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/placeholder.png";
                      }}
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 768px) 50vw, (max-width: 1024px) 25vw, 16.6vw"
                      className="object-cover transition-transform duration-500 ease-in-out group-hover:scale-105"
                    />
                    {game.isNew && (
                      <Badge className="absolute top-2 right-2 px-2 py-0.5 text-xs" variant="secondary">
                        NEW!
                      </Badge>
                    )}
                     <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-100 group-hover:opacity-0 transition-opacity duration-300"></div>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-semibold line-clamp-2 text-foreground text-sm md:text-base group-hover:text-primary transition-colors">
                      {game.title}
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 mt-1">
                      {game.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
           !initialErrorMsg && <p className="text-center text-muted-foreground py-10">이 카테고리에는 게임이 없습니다.</p>
        )}

      {/* Load More Button */}
      {expandable && allGames.length >= depth * unit && (
        <div className="mt-8 flex justify-center">
          <Button onClick={onLoadMoreGamesButtonClicked} disabled={isLoadingMore}>
            {isLoadingMore ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-primary" /> 로딩 중...
              </>
            ) : (
              '더보기'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
